import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, LinearProgress, Link, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, Typography, alpha, useTheme,
} from '@mui/material';
import { Add, PointOfSaleOutlined } from '@mui/icons-material';
import financeService from '../../../services/financeService';
import { CashCount, CashCountStatus } from '../../../types/finance';
import { Pagination } from '../../../types';
import { useTimezone } from '../../../hooks/useTimezone';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import SearchableSelect from '../../common/SearchableSelect';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import EmptyState from '../../common/EmptyState';
import JournalEntryDrawer from '../journal/JournalEntryDrawer';
import Amount from '../ledger/Amount';
import CountCashDialog from './CountCashDialog';
import { CASH_COUNT_STATUS_OPTIONS, CashCountStatusChip, CashDifference } from './CashCountStatus';
import { useCashCountSetup } from './useCashCountSetup';

/**
 * Cash & bank → Cash counts: every day-close count, newest first, with what
 * the books expected, what was counted and what happened to the difference.
 */
const CashCountsPanel: React.FC = () => {
  const theme = useTheme();
  const { formatDate } = useTimezone();
  const { canWrite } = useModulePermissions();
  const { tills, requiresApproval, error: setupError } = useCashCountSetup();
  const [accountId, setAccountId] = useState('');
  const [status, setStatus] = useState<CashCountStatus | ''>('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [data, setData] = useState<{ items: CashCount[]; pagination: Pagination } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counting, setCounting] = useState(false);
  const [openEntryId, setOpenEntryId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await financeService.listCashCounts({ accountId, status: status || undefined, page: page + 1, limit }));
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load cash counts.');
    } finally {
      setLoading(false);
    }
  }, [accountId, status, page, limit]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { setPage(0); }, [accountId, status]);

  const items = data?.items ?? [];
  const filtered = !!(accountId || status);
  const canCount = canWrite('cash_count') && tills.length > 0;

  return (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <SearchableSelect label="Till" value={accountId} onChange={setAccountId} options={tills} placeholder="All tills" minWidth={220} />
          <SearchableSelect label="Status" value={status} onChange={v => setStatus(v as CashCountStatus | '')} options={CASH_COUNT_STATUS_OPTIONS} placeholder="All statuses" minWidth={200} />
          {filtered && <Button size="small" onClick={() => { setAccountId(''); setStatus(''); }}>Reset</Button>}
          <Box sx={{ flex: 1 }} />
          {canWrite('cash_count') && (
            <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => setCounting(true)} disabled={!canCount}>
              Count cash
            </Button>
          )}
        </Stack>
      </Paper>

      {(setupError || error) && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{setupError || error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
        {loading && items.length > 0 && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2 }} />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell sx={{ width: 130 }}>Day</TableCell>
                <TableCell>Till</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Books expect</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Counted</TableCell>
                <TableCell align="right" sx={{ width: 160 }}>Difference</TableCell>
                <TableCell sx={{ width: 170 }}>Status</TableCell>
                <TableCell sx={{ width: 150 }}>Voucher</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && items.length === 0 ? <TableRowSkeletons columns={7} /> : items.map(c => (
                <TableRow key={c._id} hover>
                  <TableCell><Typography variant="body2">{formatDate(c.day)}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{c.accountName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Counted by {c.countedByName ?? 'Unknown'}{c.note ? ` · ${c.note}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right"><Amount paisa={c.expectedPaisa} blankZero={false} /></TableCell>
                  <TableCell align="right"><Amount paisa={c.countedPaisa} blankZero={false} fontWeight={600} /></TableCell>
                  <TableCell align="right"><CashDifference paisa={c.differencePaisa} /></TableCell>
                  <TableCell><CashCountStatusChip count={c} /></TableCell>
                  <TableCell>
                    {c.journalEntryId && c.entryNo ? (
                      <Link component="button" variant="body2" underline="hover" onClick={() => setOpenEntryId(c.journalEntryId)}
                        sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {c.entryNo}
                      </Link>
                    ) : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && items.length === 0 && (
          <Box sx={{ p: 3 }}>
            <EmptyState
              icon={<PointOfSaleOutlined />}
              title={error ? 'Cash counts could not load' : filtered ? 'No counts match' : 'No cash counted yet'}
              description={error || (filtered ? 'Clear a filter to see more.' : 'Count each till at the end of the day; any difference with the books is recorded here.')}
              action={!filtered && !error && canCount ? { label: 'Count cash', onClick: () => setCounting(true), icon: <Add /> } : undefined}
              dense
            />
          </Box>
        )}

        <TablePagination
          component="div"
          count={data?.pagination.totalItems ?? 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={limit}
          onRowsPerPageChange={e => { setLimit(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <CountCashDialog
        open={counting}
        onClose={() => setCounting(false)}
        onSaved={() => setRefreshKey(k => k + 1)}
        tills={tills}
        requiresApproval={requiresApproval}
      />
      <JournalEntryDrawer entryId={openEntryId} onClose={() => setOpenEntryId(null)} onChanged={() => setRefreshKey(k => k + 1)} />
    </Box>
  );
};

export default CashCountsPanel;
